"use client";

import React, { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import {
  MapPin,
  Mail,
  Phone,
  User,
  ArrowLeft,
  CheckCircle2,
  Wrench,
  Shield,
  ChevronLeft,
  ChevronRight,
  Building2,
  Loader2,
  Lock,
  X,
  Clock,
  Truck,
  Calendar,
  Info,
  Sparkles,
} from "lucide-react";
import { useAuth } from "@/modules/auth/context/AuthContext";
import { useServiceDetailPage } from "@/modules/public/home/hooks/usePublicData";

interface ServicesDetailClientProps {
  slug: string;
  categorySlug: string;
  subcatSlug: string;
}

export default function ServicesDetailClient({
  slug,
  categorySlug,
  subcatSlug,
}: ServicesDetailClientProps) {
  const router = useRouter();
  const { user } = useAuth();

  const {
    service,
    loading,
    error,
    buyerName,
    setBuyerName,
    buyerPhone,
    setBuyerPhone,
    buyerEmail,
    setBuyerEmail,
    buyerLocation,
    setBuyerLocation,
    message,
    setMessage,
    submitting,
    submitSuccess,
    setSubmitSuccess,
    submitError,
    selectedVariantIndex,
    setSelectedVariantIndex,
    isEnquiryModalOpen,
    setIsEnquiryModalOpen,
    handleEnquirySubmit,
  } = useServiceDetailPage(slug);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const serviceImages = useMemo(() => {
    const list: string[] = [];
    if (service) {
      if (Array.isArray(service.images)) {
        service.images.forEach((img: any) => {
          if (img && typeof img === "string") {
            list.push(img);
          }
        });
      }
      if (list.length === 0 && service.imageUrl) {
        list.push(service.imageUrl);
      }
    }
    return list;
  }, [service]);

  useEffect(() => {
    if (serviceImages.length <= 1 || isHovered) return;
    const timer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % serviceImages.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [serviceImages, isHovered]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-slate-50/50">
        <Loader2 className="w-12 h-12 text-[#0f9648] animate-spin mb-4" />
        <p className="text-slate-500 font-bold uppercase tracking-wider text-xs">Fetching service details...</p>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 bg-slate-50/50">
        <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-200 text-center max-w-md">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Service Not Found</h2>
          <p className="text-slate-600 mb-6">{error || "The requested service could not be found."}</p>
          <button
            onClick={() => router.back()}
            className="inline-flex items-center gap-2 bg-[#0f9648] text-white font-bold px-6 py-2.5 rounded-sm hover:bg-green-700 transition cursor-pointer border-0"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </div>
    );
  }

  const priorBookingVal = service.advanceBooking || "No";
  const transportationVal = service.transportation || "Not Required";
  const serviceAreaVal = Array.isArray(service.districtsServed) ? service.districtsServed.join(", ") : "—";

  return (
    <div className="bg-white min-h-screen pb-16">
      {/* Breadcrumbs Banner */}
      <div className="bg-white border-b border-slate-100 py-3 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-xs sm:text-sm text-slate-500 overflow-x-auto whitespace-nowrap">
            <Link href="/" className="hover:text-green-700 transition-colors font-medium">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            <Link href={`/${categorySlug}`} className="hover:text-green-700 transition-colors font-medium">
              {service.category?.name || "Services"}
            </Link>
            <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            <Link
              href={`/${categorySlug}/${subcatSlug}`}
              className="hover:text-green-700 transition-colors font-medium"
            >
              {service.subcategory?.name || "Subcategory"}
            </Link>
            <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            <span className="font-bold text-emerald-800 truncate max-w-xs">{service.title}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <button
          onClick={() => router.back()}
          className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900 font-bold mb-6 group transition uppercase tracking-wider bg-transparent border-0 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Back
        </button>

        {/* 2-Column Dashboard-style Service Header Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          
          {/* Column 1: Image & Highlights (40% width) */}
          <div className="lg:col-span-5 flex flex-col justify-between h-full space-y-6">
            <div 
              className="relative rounded-sm border border-slate-200 bg-white flex items-center justify-center min-h-[300px] lg:min-h-[380px] overflow-hidden group shadow-xs w-full cursor-pointer"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {serviceImages.length > 0 ? (
                <>
                  <Image
                    src={serviceImages[currentImageIndex]}
                    alt={`${service.title} - Image ${currentImageIndex + 1}`}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 40vw, 33vw"
                    priority
                    className="object-cover hover:scale-102 transition-all duration-500"
                  />
                  {serviceImages.length > 1 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex(
                            (prev) => (prev - 1 + serviceImages.length) % serviceImages.length
                          );
                        }}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-1.5 rounded-full transition opacity-0 group-hover:opacity-100 cursor-pointer border-0 flex items-center justify-center z-10"
                        aria-label="Previous image"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex((prev) => (prev + 1) % serviceImages.length);
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-1.5 rounded-full transition opacity-0 group-hover:opacity-100 cursor-pointer border-0 flex items-center justify-center z-10"
                        aria-label="Next image"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>

                      {/* Dot Indicators */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10 bg-black/30 px-3 py-1.5 rounded-full backdrop-blur-xs">
                        {serviceImages.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentImageIndex(idx);
                            }}
                            className={`w-1.5 h-1.5 rounded-full transition-all border-0 p-0 cursor-pointer ${
                              currentImageIndex === idx ? "bg-white scale-125" : "bg-white/40"
                            }`}
                            aria-label={`Go to slide ${idx + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center gap-2 text-slate-300 p-6">
                  <Wrench className="w-20 h-20" />
                  <span className="text-[10px] font-bold tracking-widest uppercase">Service Listing</span>
                </div>
              )}
              <div className="absolute top-4 left-4 bg-emerald-700 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-sm shadow-sm flex items-center gap-1 z-10">
                <Shield className="w-3.5 h-3.5" /> Verified Partner
              </div>
            </div>

            {/* Visual Specs Badge Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white border border-slate-200 p-3 rounded-sm text-center shadow-xs">
                <Clock className="w-5 h-5 mx-auto text-emerald-600 mb-1" />
                <span className="block text-[8px] font-black text-slate-400 uppercase tracking-widest">Booking</span>
                <span className="block text-xs font-bold text-slate-800 mt-0.5 truncate" title={priorBookingVal}>
                  {priorBookingVal}
                </span>
              </div>
              <div className="bg-white border border-slate-200 p-3 rounded-sm text-center shadow-xs">
                <Truck className="w-5 h-5 mx-auto text-emerald-600 mb-1" />
                <span className="block text-[8px] font-black text-slate-400 uppercase tracking-widest">Transport</span>
                <span className="block text-xs font-bold text-slate-800 mt-0.5 truncate" title={transportationVal}>
                  {transportationVal}
                </span>
              </div>
              <div className="bg-white border border-slate-200 p-3 rounded-sm text-center shadow-xs">
                <MapPin className="w-5 h-5 mx-auto text-emerald-600 mb-1" />
                <span className="block text-[8px] font-black text-slate-400 uppercase tracking-widest">Coverage</span>
                <span className="block text-xs font-bold text-slate-800 mt-0.5 truncate" title={serviceAreaVal}>
                  {serviceAreaVal}
                </span>
              </div>
            </div>
          </div>

          {/* Column 2: Service Primary Info (70% width) */}
          <div className="lg:col-span-7 flex flex-col justify-between bg-white border border-slate-250 rounded-sm p-6 sm:p-8 shadow-xs">
            <div className="space-y-6">
              <div className="space-y-2">
               
                <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight leading-tight">
                  {service.title}
                </h1>
                {service.tagline && (
                  <p className="text-sm font-bold text-emerald-800 leading-normal">
                    {service.tagline}
                  </p>
                )}
              </div>

              {user ? (
                <div className="p-4 bg-emerald-50/50 border border-emerald-100 rounded-sm space-y-3">
                  <div className="space-y-1">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Estimated Charges</span>
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="text-3xl font-black text-slate-900">₹{service.applicableFee}</span>
                      <span className="text-slate-500 font-bold text-sm">per {service.feeUnit || "Acre"}</span>
                      {service.feeType && (
                        <span className="bg-emerald-100 text-emerald-800 text-[10px] font-black px-2 py-0.5 rounded-sm uppercase tracking-wider">
                          {service.feeType}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Reference service provider charges</p>
                  </div>

                  <div className="border-t border-emerald-100/50 pt-2 flex justify-between text-xs text-emerald-800 font-bold">
                    <span>Category: {service.category?.name || "Agri Services"}</span>
                    <span>Subcat: {service.subcategory?.name}</span>
                  </div>
                </div>
              ) : (
                /* Locked Block for Anonymous Visitors */
                <div className="bg-slate-50 rounded-sm p-6 border border-slate-200 space-y-4">
                  <div className="flex items-start gap-3 relative z-10">
                    <Lock className="w-8 h-8 text-slate-400 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-black text-sm sm:text-base tracking-tight text-slate-900 flex items-center gap-1.5 uppercase">
                        Access Restricted
                      </h4>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                        Pricing matrices, service operational areas, provider details, and booking/quote options require a verified buyer account.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <Link
                      href="/auth/login"
                      className="flex-1 text-center bg-[#0f9648] hover:bg-green-700 text-white font-bold py-2.5 rounded-sm text-xs uppercase tracking-wider transition shadow-sm"
                    >
                      Login to Unlock
                    </Link>
                    <Link
                      href="/auth/register"
                      className="flex-1 text-center bg-transparent border border-slate-350 text-slate-900 font-bold py-2.5 rounded-sm text-xs uppercase tracking-wider transition hover:bg-slate-100"
                    >
                      Register as Buyer
                    </Link>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {user && (
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    onClick={() => setIsEnquiryModalOpen(true)}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#0f9648] hover:bg-green-750 text-white font-bold py-3.5 px-6 rounded-sm shadow-xs transition text-xs sm:text-sm cursor-pointer uppercase tracking-wider border-0"
                  >
                    <Calendar className="w-4 h-4 shrink-0" /> Book / Request Quote
                  </button>

                  {service.mobile && (
                    <a
                      href={`tel:${service.mobile}`}
                      className="flex-1 flex items-center justify-center gap-2 bg-[#0f9648] hover:bg-green-700 text-white font-bold py-3.5 px-6 rounded-sm shadow-xs transition text-xs sm:text-sm text-center cursor-pointer uppercase tracking-wider border-0"
                    >
                      <Phone className="w-4 h-4 shrink-0" /> Call Vendor
                    </a>
                  )}
                </div>
              )}

              {user && service.vendorDetails?.vendorId && !service.vendorDetails?.hideFromStore && (
                <Link
                  href={`/vendor-profile/${service.vendorDetails.slug || service.vendorDetails.vendorId}`}
                  className="flex items-center justify-center gap-2 border border-slate-200 text-slate-700 hover:bg-slate-50 font-bold py-2.5 px-6 rounded-sm transition text-xs uppercase tracking-wider cursor-pointer"
                >
                  <Building2 className="w-4 h-4 shrink-0" />
                  View Vendor Profile
                  <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>

            {/* Description Text */}
            <div className="pt-6 border-t border-slate-100 space-y-2 mt-6">
              <h3 className="font-black text-slate-900 text-xs sm:text-sm uppercase tracking-wider">Service Overview</h3>
              <p className="text-slate-700 text-justify leading-relaxed text-sm whitespace-pre-line font-medium">
                {service.description || "No service overview details provided."}
              </p>
            </div>
          </div>
        </div>

        {/* Dynamic & Built-in Specifications */}
        <div className="space-y-10 mt-12">
          <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-xs space-y-4">
            <div className="relative pb-2.5">
              <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">Service & Provider Specifications</h2>
              <div className="absolute bottom-0 left-0 w-12 h-[2.5px] bg-[#0f9648] rounded-full" />
            </div>

            <div className="w-full border-t border-slate-150 divide-y divide-slate-150">
              <div className="flex hover:bg-slate-50/50 transition-colors">
                <div className="w-1/3 py-3.5 pr-6 font-bold text-slate-500 align-top text-xs uppercase tracking-wider">
                  Provider Name
                </div>
                <div className="w-2/3 py-3.5 pl-6 text-slate-900 font-bold text-xs sm:text-sm">
                  {service.providerName}
                </div>
              </div>

              {service.contactPerson && (
                <div className="flex hover:bg-slate-50/50 transition-colors">
                  <div className="w-1/3 py-3.5 pr-6 font-bold text-slate-500 align-top text-xs uppercase tracking-wider">
                    Contact Person
                  </div>
                  <div className="w-2/3 py-3.5 pl-6 text-slate-900 font-bold text-xs sm:text-sm">
                    {service.contactPerson}
                  </div>
                </div>
              )}

              <div className="flex hover:bg-slate-50/50 transition-colors">
                <div className="w-1/3 py-3.5 pr-6 font-bold text-slate-500 align-top text-xs uppercase tracking-wider">
                  Provider Type
                </div>
                <div className="w-2/3 py-3.5 pl-6 text-slate-900 font-bold text-xs sm:text-sm">
                  {service.providerType}
                </div>
              </div>

              <div className="flex hover:bg-slate-50/50 transition-colors">
                <div className="w-1/3 py-3.5 pr-6 font-bold text-slate-500 align-top text-xs uppercase tracking-wider">
                  Experience
                </div>
                <div className="w-2/3 py-3.5 pl-6 text-slate-900 font-bold text-xs sm:text-sm">
                  {service.yearsOfExperience} Years
                </div>
              </div>



              <div className="flex hover:bg-slate-50/50 transition-colors">
                <div className="w-1/3 py-3.5 pr-6 font-bold text-slate-500 align-top text-xs uppercase tracking-wider">
                  Service Availability
                </div>
                <div className="w-2/3 py-3.5 pl-6 text-slate-900 font-bold text-xs sm:text-sm">
                  {service.serviceAvailability}
                </div>
              </div>

              {service.cropsCovered && service.cropsCovered.length > 0 && (
                <div className="flex hover:bg-slate-50/50 transition-colors">
                  <div className="w-1/3 py-3.5 pr-6 font-bold text-slate-500 align-top text-xs uppercase tracking-wider">
                    Crops / Activities Covered
                  </div>
                  <div className="w-2/3 py-3.5 pl-6 text-slate-900 font-bold text-xs sm:text-sm">
                    {service.cropsCovered.join(", ")}
                  </div>
                </div>
              )}

              <div className="flex hover:bg-slate-50/50 transition-colors">
                <div className="w-1/3 py-3.5 pr-6 font-bold text-slate-500 align-top text-xs uppercase tracking-wider">
                  Booking Conditions
                </div>
                <div className="w-2/3 py-3.5 pl-6 text-slate-900 font-bold text-xs sm:text-sm">
                  {service.advanceBooking === "Yes" ? "Prior booking required" : "No prior booking required"}
                </div>
              </div>

              <div className="flex hover:bg-slate-50/50 transition-colors">
                <div className="w-1/3 py-3.5 pr-6 font-bold text-slate-500 align-top text-xs uppercase tracking-wider">
                  Transportation
                </div>
                <div className="w-2/3 py-3.5 pl-6 text-slate-900 font-bold text-xs sm:text-sm">
                  {service.transportation}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enquiry Form Modal */}
      {isEnquiryModalOpen && user && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white rounded-sm shadow-2xl max-w-lg w-full mx-auto overflow-hidden border border-slate-200 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="bg-emerald-900 p-5 text-white flex justify-between items-center">
              <div>
                <h3 className="font-black text-base tracking-wider uppercase">Request Service Booking</h3>
                <p className="text-[10px] text-emerald-200 mt-1">
                  Submit this form to send a direct request to the service provider.
                </p>
              </div>
              <button
                onClick={() => setIsEnquiryModalOpen(false)}
                className="text-white/80 hover:text-white transition cursor-pointer p-1.5 rounded-full hover:bg-emerald-950 border-0 bg-transparent"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 max-h-[80vh] overflow-y-auto">
              {submitSuccess ? (
                <div className="text-center py-8 px-4 space-y-4">
                  <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto border border-emerald-100">
                    <CheckCircle2 className="w-8 h-8 text-[#0f9648]" />
                  </div>
                  <h4 className="text-base font-black text-slate-900 uppercase tracking-wide">Request Submitted!</h4>
                  <p className="text-xs text-slate-600 max-w-sm mx-auto font-medium">
                    Thank you. The service provider,{" "}
                    <strong className="text-slate-900">{service.vendorDetails?.name}</strong>, has been notified
                    and will reach out to you directly at the phone number provided.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitSuccess(false);
                      setIsEnquiryModalOpen(false);
                    }}
                    className="mt-4 bg-[#0f9648] hover:bg-green-700 text-white font-bold px-6 py-2 rounded-sm text-xs uppercase tracking-wider transition shadow-sm cursor-pointer border-0"
                  >
                    Close Window
                  </button>
                </div>
              ) : (
                <form onSubmit={handleEnquirySubmit} className="space-y-4">
                  {submitError && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-sm font-semibold">
                      {submitError}
                    </div>
                  )}

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        required
                        value={buyerName}
                        onChange={(e) => setBuyerName(e.target.value)}
                        className="w-full text-xs font-bold text-slate-900 border border-slate-200 rounded-sm pl-9 pr-3 py-2.5 focus:outline-none focus:border-[#0f9648] bg-slate-50 placeholder-slate-400"
                        placeholder="Enter your name"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <input
                        type="tel"
                        required
                        value={buyerPhone}
                        onChange={(e) => setBuyerPhone(e.target.value)}
                        className="w-full text-xs font-bold text-slate-900 border border-slate-200 rounded-sm pl-9 pr-3 py-2.5 focus:outline-none focus:border-[#0f9648] bg-slate-50 placeholder-slate-400"
                        placeholder="e.g. +91 9876543210"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <input
                        type="email"
                        value={buyerEmail}
                        onChange={(e) => setBuyerEmail(e.target.value)}
                        className="w-full text-xs font-bold text-slate-900 border border-slate-200 rounded-sm pl-9 pr-3 py-2.5 focus:outline-none focus:border-[#0f9648] bg-slate-50 placeholder-slate-400"
                        placeholder="e.g. buyer@example.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                      Your Location (Taluk / District)
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={buyerLocation}
                        onChange={(e) => setBuyerLocation(e.target.value)}
                        className="w-full text-xs font-bold text-slate-900 border border-slate-200 rounded-sm pl-9 pr-3 py-2.5 focus:outline-none focus:border-[#0f9648] bg-slate-50 placeholder-slate-400"
                        placeholder="e.g. Maddur, Mandya"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                      Enquiry Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={4}
                      required
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="w-full text-xs font-bold text-slate-900 border border-slate-200 rounded-sm p-3 focus:outline-none focus:border-[#0f9648] bg-slate-50 placeholder-slate-400"
                      placeholder="Specify your requirements like date of booking, service hours required etc."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full mt-2 bg-[#0f9648] hover:bg-green-750 text-white font-bold py-3 rounded-sm text-xs uppercase tracking-wider transition shadow-sm flex items-center justify-center gap-2 cursor-pointer border-0"
                  >
                    {submitting ? (
                      <Loader2 className="w-4.5 h-4.5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4.5 h-4.5" />
                    )}
                    Send Request
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
